import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/rootReducer';

import getRoles from 'components/UserProfileOnboarding/components/OnboardingForm/components/Role/getRoles';
import getLabels from 'components/UserProfileOnboarding/components/OnboardingForm/components/Division/getLabels';

import createAuthPath from 'helpers/createAuthPath';
import { setOpusAccessBannerView } from 'redux/reducers/user/opusAccessBanner';
import { IAuthenticationProfile } from 'redux/reducers/authentication';

import { ReactComponent as UserIcon } from 'icons/user-icon.svg';

import { DropdownButton } from 'components/Header/TopBar/TopBar.style';
import {
  StyledDropdownListWrapper,
  DropdownListContent,
} from 'components/Header/CompareDropdown/CompareDropdown';
import { Label } from 'components/Header/CompareDropdown/SubDropdownList';

import EditModal from './EditModal';

import { TAsyncDispatchResult, ICountryFlagProps } from 'types/shared';
import {
  StyledLink,
  UserDataWrapper,
  EditButton,
  TopWrapper,
  Title,
  SubTitle,
} from './styles';

interface IUserDropdown {
  isAccessBannerShown: boolean;
  isOpen: boolean;
  toggleDropdown: Function;
  isStatic?: boolean;
  userLocation?: string | null;
  userLabel?: string[];
  userRole?: string | null;
  userDivision?: string | null;
  userProfile?: IAuthenticationProfile;
  isMobileScreen?: boolean;
}

const UserDropdown: React.FC<IUserDropdown> = (props) => {
  const {
    isAccessBannerShown,
    isOpen,
    toggleDropdown,
    isStatic,
    userLocation,
    userLabel,
    userRole,
    userDivision,
    userProfile,
    isMobileScreen,
  } = props;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const countries = useSelector((state: RootState) => state.countries);
  const roles = getRoles();
  const labels = getLabels();

  const authPath = createAuthPath();
  const dispatch = useDispatch();
  const logOut = (): TAsyncDispatchResult | boolean =>
    isAccessBannerShown && dispatch(setOpusAccessBannerView(true));

  const location = useMemo(
    () =>
      countries?.data?.find((c: ICountryFlagProps) => c.id === userLocation)
        ?.displayName,
    [countries, userLocation]
  );

  const role = useMemo(
    () => roles?.find((c: any) => c.value === userRole)?.label,
    [roles, userRole]
  );

  const selectedLabels = useMemo(
    () =>
      userLabel?.length
        ? userLabel.reduce((filtered: any, option: any) => {
            const selectedLabel = labels.find(
              (label: any) => label.value === option
            )?.label;
            if (selectedLabel) {
              filtered.push(selectedLabel);
            }
            return filtered;
          }, [])
        : [],
    [labels, userLabel]
  );

  return (
    <>
      <DropdownButton
        onClick={toggleDropdown}
        aria-haspopup="true"
        aria-expanded={isOpen}
        isOpen={isOpen}
      >
        <UserIcon />
      </DropdownButton>

      {isOpen && (
        <StyledDropdownListWrapper
          overflowLeft
          toggleDropdown={toggleDropdown}
          isStatic={isStatic}
          withoutPaddingBottom
        >
          <DropdownListContent>
            <li>
              <TopWrapper>
                <Label>My account</Label>
                {!isMobileScreen && !userProfile?.isExternal ? (
                  <EditButton onClick={(): void => setIsEditModalOpen(true)}>
                    Edit
                  </EditButton>
                ) : null}
              </TopWrapper>

              <UserDataWrapper>
                {userProfile?.name && userProfile?.email && (
                  <>
                    <Title>{userProfile.name}</Title>
                    <SubTitle>{userProfile.email}</SubTitle>
                  </>
                )}
                {location && (
                  <>
                    <Title>{location}</Title>
                    <SubTitle>Office Location</SubTitle>
                  </>
                )}
                {!!selectedLabels?.length && userDivision === 'recordedMusic' && (
                  <>
                    <Title>{selectedLabels.join(', ')}</Title>
                    <SubTitle>{`Label${
                      selectedLabels?.length > 1 ? 's' : ''
                    }`}</SubTitle>
                  </>
                )}
                {role && (
                  <>
                    <Title>{role}</Title>
                    <SubTitle>Role</SubTitle>
                  </>
                )}
              </UserDataWrapper>
              <StyledLink
                href={`${authPath}/logout`}
                className="link button"
                onClick={logOut}
              >
                Log Out
              </StyledLink>
            </li>
          </DropdownListContent>
        </StyledDropdownListWrapper>
      )}

      {isEditModalOpen && !isMobileScreen && (
        <EditModal
          userProfile={userProfile}
          setIsEditModalOpen={setIsEditModalOpen}
        />
      )}
    </>
  );
};

export default UserDropdown;
